import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Calendar, Plus, Users, Eye, EyeOff, Pencil, Trash2, Search,
  MapPin, Clock, ArrowLeft, X, CheckCircle2, QrCode, BarChart3,
  Video, Globe, Monitor, Link as LinkIcon, Wifi
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_MAP = {
  draft: { label: "Brouillon", color: "bg-[#64748b]/20 text-[#94A3B8]" },
  published: { label: "Publie", color: "bg-[#10B981]/20 text-[#10B981]" },
  cancelled: { label: "Annule", color: "bg-red-500/20 text-red-400" },
  ended: { label: "Termine", color: "bg-[#8B5CF6]/20 text-[#8B5CF6]" },
};

const FORMAT_MAP = {
  presentiel: { label: "Presentiel", icon: MapPin, color: "text-[#10B981]", bg: "bg-[#10B981]/10" },
  en_ligne: { label: "En ligne", icon: Wifi, color: "text-[#3B82F6]", bg: "bg-[#3B82F6]/10" },
  hybride: { label: "Hybride", icon: Globe, color: "text-[#8B5CF6]", bg: "bg-[#8B5CF6]/10" },
};

const PROVIDER_OPTIONS = [
  { value: "zoom", label: "Zoom" },
  { value: "google_meet", label: "Google Meet" },
  { value: "external_link", label: "Autre lien de reunion" },
  { value: "glotech_live", label: "GLOTECH Live Meeting", disabled: true, badge: "Bientot" },
];

const EMPTY_FORM = {
  title: "", description: "", event_type: "conference",
  start_date: "", end_date: "", location: "", address: "",
  max_participants: 500, registration_open: true,
  event_format: "presentiel",
  meeting_provider: "zoom",
  meeting_url: "",
  meeting_secure_link: true,
  meeting_require_auth: true,
  meeting_show_publicly: false,
  meeting_waiting_room: false,
  meeting_max_online: 0,
};

export default function OrgEventsPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const headers = { Authorization: `Bearer ${token}` };

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(searchParams.get("create") === "true");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchEvents = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/event/events`, { headers });
      setEvents(Array.isArray(data) ? data : data.events || []);
    } catch { toast.error("Erreur de chargement"); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const handleSave = async () => {
    if (!form.title) return toast.error("Le titre est obligatoire");
    setSaving(true);
    try {
      const payload = { ...form };
      // Build meeting_config if online/hybrid
      if (payload.event_format === "en_ligne" || payload.event_format === "hybride") {
        payload.meeting_config = {
          provider: payload.meeting_provider,
          meeting_url: payload.meeting_url,
          secure_link: payload.meeting_secure_link,
          require_authentication: payload.meeting_require_auth,
          show_link_publicly: payload.meeting_show_publicly,
          waiting_room: payload.meeting_waiting_room,
          max_online_participants: payload.meeting_max_online || 0,
        };
      } else {
        payload.meeting_config = null;
      }
      // Clean up frontend-only fields
      delete payload.meeting_provider;
      delete payload.meeting_url;
      delete payload.meeting_secure_link;
      delete payload.meeting_require_auth;
      delete payload.meeting_show_publicly;
      delete payload.meeting_waiting_room;
      delete payload.meeting_max_online;

      if (editing) {
        await axios.put(`${API}/api/event/events/${editing}`, payload, { headers });
        toast.success("Evenement mis a jour");
      } else {
        await axios.post(`${API}/api/event/events`, payload, { headers });
        toast.success("Evenement cree");
      }
      setShowForm(false);
      setEditing(null);
      setForm(EMPTY_FORM);
      fetchEvents();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  const handlePublish = async (evt) => {
    const newStatus = evt.status === "published" ? "draft" : "published";
    try {
      await axios.put(`${API}/api/event/events/${evt.id}`, { status: newStatus }, { headers });
      toast.success(newStatus === "published" ? "Evenement publie" : "Evenement depublie");
      fetchEvents();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cet evenement et tous ses participants ?")) return;
    try {
      await axios.delete(`${API}/api/event/events/${id}`, { headers });
      toast.success("Evenement supprime");
      fetchEvents();
    } catch { toast.error("Erreur"); }
  };

  const handleEdit = (evt) => {
    const mc = evt.meeting_config || {};
    setForm({
      title: evt.title || "", description: evt.description || "",
      event_type: evt.event_type || "conference",
      start_date: evt.start_date ? evt.start_date.slice(0, 16) : "",
      end_date: evt.end_date ? evt.end_date.slice(0, 16) : "",
      location: evt.location || "", address: evt.address || "",
      max_participants: evt.max_participants || 500,
      registration_open: evt.registration_open !== false,
      event_format: evt.event_format || "presentiel",
      meeting_provider: mc.provider || "zoom",
      meeting_url: mc.meeting_url || "",
      meeting_secure_link: mc.secure_link !== false,
      meeting_require_auth: mc.require_authentication !== false,
      meeting_show_publicly: mc.show_link_publicly || false,
      meeting_waiting_room: mc.waiting_room || false,
      meeting_max_online: mc.max_online_participants || 0,
    });
    setEditing(evt.id);
    setShowForm(true);
  };

  const filtered = events.filter(e =>
    e.title?.toLowerCase().includes(search.toLowerCase()) ||
    e.location?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0099CC]" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="org-events-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Mes evenements</h1>
          <p className="text-sm text-[#64748b]">{events.length} evenement{events.length > 1 ? "s" : ""}</p>
        </div>
        <Button data-testid="new-event-btn" onClick={() => { setForm(EMPTY_FORM); setEditing(null); setShowForm(true); }} className="bg-[#0099CC] hover:bg-[#00AADB] text-white">
          <Plus className="h-4 w-4 mr-2" />
          Nouvel evenement
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4A5568]" />
        <Input
          data-testid="event-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher un evenement..."
          className="pl-10 bg-[#0B0F19] border-[#2A3441] text-white"
        />
      </div>

      {/* Create/Edit Form */}
      {showForm && (
        <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-bold text-white">{editing ? "Modifier l'evenement" : "Creer un evenement"}</h2>
            <button onClick={() => { setShowForm(false); setEditing(null); }} className="text-[#64748b] hover:text-white"><X className="h-5 w-5" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="text-sm text-[#94A3B8] mb-1 block">Titre *</label>
              <Input data-testid="event-title-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Ex: Conference Tech 2026" className="bg-[#151B2B] border-[#2A3441] text-white" />
            </div>
            <div className="md:col-span-2">
              <label className="text-sm text-[#94A3B8] mb-1 block">Description</label>
              <Textarea data-testid="event-desc-input" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Decrivez votre evenement..." className="bg-[#151B2B] border-[#2A3441] text-white min-h-[80px]" />
            </div>

            {/* Format selector */}
            <div className="md:col-span-2">
              <label className="text-sm text-[#94A3B8] mb-2 block">Format de l'evenement</label>
              <div className="grid grid-cols-3 gap-3" data-testid="event-format-selector">
                {Object.entries(FORMAT_MAP).map(([key, { label, icon: Icon, color, bg }]) => (
                  <button key={key} type="button" data-testid={`format-${key}`}
                    onClick={() => setForm({ ...form, event_format: key })}
                    className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all ${form.event_format === key ? `border-current ${color} ${bg}` : "border-[#2A3441] text-[#64748b] hover:border-[#4A5568]"}`}>
                    <Icon className="h-5 w-5" />
                    <span className="text-sm font-medium">{label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-sm text-[#94A3B8] mb-1 block">Type</label>
              <select data-testid="event-type-select" value={form.event_type} onChange={e => setForm({ ...form, event_type: e.target.value })} className="w-full h-10 px-3 rounded-md bg-[#151B2B] border border-[#2A3441] text-white text-sm">
                <option value="conference">Conference</option>
                <option value="seminaire">Seminaire</option>
                <option value="formation">Formation</option>
                <option value="ceremonie">Ceremonie</option>
                <option value="concert">Concert / Spectacle</option>
                <option value="gala">Gala</option>
                <option value="webinaire">Webinaire</option>
                <option value="reunion">Reunion</option>
                <option value="autre">Autre</option>
              </select>
            </div>
            <div>
              <label className="text-sm text-[#94A3B8] mb-1 block">Places max {form.event_format !== "en_ligne" ? "(salle)" : "(en ligne)"}</label>
              <Input data-testid="event-max-input" type="number" value={form.max_participants} onChange={e => setForm({ ...form, max_participants: parseInt(e.target.value) || 0 })} className="bg-[#151B2B] border-[#2A3441] text-white" />
            </div>
            <div>
              <label className="text-sm text-[#94A3B8] mb-1 block">Date de debut</label>
              <Input data-testid="event-start-input" type="datetime-local" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} className="bg-[#151B2B] border-[#2A3441] text-white" />
            </div>
            <div>
              <label className="text-sm text-[#94A3B8] mb-1 block">Date de fin</label>
              <Input data-testid="event-end-input" type="datetime-local" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} className="bg-[#151B2B] border-[#2A3441] text-white" />
            </div>

            {/* Location fields - show for presentiel and hybride */}
            {form.event_format !== "en_ligne" && (
              <>
                <div>
                  <label className="text-sm text-[#94A3B8] mb-1 block">Lieu</label>
                  <Input data-testid="event-location-input" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="Ex: Palais des Congres" className="bg-[#151B2B] border-[#2A3441] text-white" />
                </div>
                <div>
                  <label className="text-sm text-[#94A3B8] mb-1 block">Adresse</label>
                  <Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="Adresse complete" className="bg-[#151B2B] border-[#2A3441] text-white" />
                </div>
              </>
            )}

            {/* Meeting config - show for en_ligne and hybride */}
            {(form.event_format === "en_ligne" || form.event_format === "hybride") && (
              <div className="md:col-span-2 bg-[#0B1120] border border-[#1E3A5F] rounded-xl p-5 space-y-4" data-testid="meeting-config-section">
                <div className="flex items-center gap-2 mb-1">
                  <Video className="h-4 w-4 text-[#3B82F6]" />
                  <span className="text-sm font-semibold text-[#3B82F6]">Configuration reunion en ligne</span>
                </div>

                <div>
                  <label className="text-sm text-[#94A3B8] mb-1 block">Fournisseur de visioconference</label>
                  <div className="grid grid-cols-2 gap-2" data-testid="meeting-provider-selector">
                    {PROVIDER_OPTIONS.map(opt => (
                      <button key={opt.value} type="button" disabled={opt.disabled}
                        data-testid={`provider-${opt.value}`}
                        onClick={() => !opt.disabled && setForm({ ...form, meeting_provider: opt.value })}
                        className={`flex items-center justify-center gap-2 p-3 rounded-lg border transition-all text-sm ${
                          opt.disabled ? "border-[#2A3441] text-[#4A5568] cursor-not-allowed opacity-50" :
                          form.meeting_provider === opt.value ? "border-[#3B82F6] text-[#3B82F6] bg-[#3B82F6]/10" : "border-[#2A3441] text-[#94A3B8] hover:border-[#4A5568]"
                        }`}>
                        {opt.label}
                        {opt.badge && <span className="text-[10px] bg-[#F59E0B]/20 text-[#F59E0B] px-1.5 py-0.5 rounded-full">{opt.badge}</span>}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-sm text-[#94A3B8] mb-1 block">
                    {form.meeting_provider === "zoom" ? "Lien de la reunion Zoom" :
                     form.meeting_provider === "google_meet" ? "Lien Google Meet" : "Lien de la reunion"}
                  </label>
                  <Input data-testid="meeting-url-input" value={form.meeting_url} onChange={e => setForm({ ...form, meeting_url: e.target.value })}
                    placeholder={form.meeting_provider === "zoom" ? "https://zoom.us/j/..." : form.meeting_provider === "google_meet" ? "https://meet.google.com/..." : "https://..."}
                    className="bg-[#151B2B] border-[#2A3441] text-white" />
                </div>

                {form.event_format === "hybride" && (
                  <div>
                    <label className="text-sm text-[#94A3B8] mb-1 block">Places max en ligne (0 = illimite)</label>
                    <Input data-testid="meeting-max-online" type="number" value={form.meeting_max_online} onChange={e => setForm({ ...form, meeting_max_online: parseInt(e.target.value) || 0 })} className="bg-[#151B2B] border-[#2A3441] text-white" />
                  </div>
                )}

                <div className="space-y-2.5 pt-1">
                  <div className="flex items-center gap-3">
                    <input type="checkbox" id="secure_link" checked={form.meeting_secure_link} onChange={e => setForm({ ...form, meeting_secure_link: e.target.checked })} className="rounded border-[#2A3441]" />
                    <label htmlFor="secure_link" className="text-sm text-[#94A3B8]">Lien securise par participant (recommande)</label>
                  </div>
                  <div className="flex items-center gap-3">
                    <input type="checkbox" id="require_auth" checked={form.meeting_require_auth} onChange={e => setForm({ ...form, meeting_require_auth: e.target.checked })} className="rounded border-[#2A3441]" />
                    <label htmlFor="require_auth" className="text-sm text-[#94A3B8]">Inscription requise pour acceder</label>
                  </div>
                  <div className="flex items-center gap-3">
                    <input type="checkbox" id="show_publicly" checked={form.meeting_show_publicly} onChange={e => setForm({ ...form, meeting_show_publicly: e.target.checked })} className="rounded border-[#2A3441]" />
                    <label htmlFor="show_publicly" className="text-sm text-[#94A3B8]">Afficher le lien publiquement</label>
                  </div>
                  <div className="flex items-center gap-3">
                    <input type="checkbox" id="waiting_room" checked={form.meeting_waiting_room} onChange={e => setForm({ ...form, meeting_waiting_room: e.target.checked })} className="rounded border-[#2A3441]" />
                    <label htmlFor="waiting_room" className="text-sm text-[#94A3B8]">Salle d'attente avant le debut</label>
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3 md:col-span-2">
              <input type="checkbox" id="reg_open" checked={form.registration_open} onChange={e => setForm({ ...form, registration_open: e.target.checked })} className="rounded border-[#2A3441]" />
              <label htmlFor="reg_open" className="text-sm text-[#94A3B8]">Inscriptions ouvertes</label>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={() => { setShowForm(false); setEditing(null); }} className="border-[#2A3441] text-[#94A3B8]">Annuler</Button>
            <Button data-testid="save-event-btn" onClick={handleSave} disabled={saving} className="bg-[#0099CC] hover:bg-[#00AADB] text-white">
              {saving ? "Enregistrement..." : editing ? "Mettre a jour" : "Creer l'evenement"}
            </Button>
          </div>
        </div>
      )}

      {/* Events Grid */}
      {filtered.length === 0 && !showForm ? (
        <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-10 text-center">
          <Calendar className="h-12 w-12 text-[#2A3441] mx-auto mb-4" />
          <h3 className="text-white font-semibold mb-2">Aucun evenement</h3>
          <p className="text-[#64748b] text-sm">Creez votre premier evenement pour commencer.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(evt => {
            const st = STATUS_MAP[evt.status] || STATUS_MAP.draft;
            const registered = evt.total_registered || evt.registered_count || 0;
            const fmt = FORMAT_MAP[evt.event_format] || FORMAT_MAP.presentiel;
            const FmtIcon = fmt.icon;
            return (
              <div key={evt.id} data-testid="event-card" className="bg-[#0B0F19] border border-[#1E293B] rounded-xl overflow-hidden hover:border-[#2A3441] transition-all">
                <div className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${st.color}`}>{st.label}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium flex items-center gap-1 ${fmt.bg} ${fmt.color}`} data-testid="event-format-badge">
                        <FmtIcon className="h-2.5 w-2.5" />{fmt.label}
                      </span>
                      {evt.recording_url && evt.status === "ended" && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full font-medium bg-[#8B5CF6]/10 text-[#8B5CF6] flex items-center gap-1" data-testid="replay-badge">
                          <Video className="h-2.5 w-2.5" />Replay
                        </span>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => handlePublish(evt)} className="p-1.5 rounded-lg hover:bg-white/5 text-[#94A3B8] hover:text-white" title={evt.status === "published" ? "Depublier" : "Publier"}>
                        {evt.status === "published" ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                      </button>
                      <button onClick={() => handleEdit(evt)} className="p-1.5 rounded-lg hover:bg-white/5 text-[#94A3B8] hover:text-white" title="Modifier">
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => handleDelete(evt.id)} className="p-1.5 rounded-lg hover:bg-red-500/10 text-[#94A3B8] hover:text-red-400" title="Supprimer">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                  <h3 className="font-bold text-white mb-2 truncate">{evt.title}</h3>
                  {evt.start_date && (
                    <div className="flex items-center gap-1.5 text-xs text-[#64748b] mb-1">
                      <Clock className="h-3 w-3" />
                      {new Date(evt.start_date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
                    </div>
                  )}
                  {evt.location && (
                    <div className="flex items-center gap-1.5 text-xs text-[#64748b] mb-3">
                      <MapPin className="h-3 w-3" />
                      {evt.location}
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-3 border-t border-[#1E293B]">
                    <div className="flex items-center gap-4 text-sm">
                      <span className="flex items-center gap-1 text-[#94A3B8]"><Users className="h-3.5 w-3.5" />{registered}</span>
                      <span className="flex items-center gap-1 text-[#94A3B8]"><CheckCircle2 className="h-3.5 w-3.5" />{evt.checked_in_count || 0}</span>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => navigate(`/app/evenements/${evt.id}`)} className="text-[#0099CC] hover:text-[#00AADB] text-xs h-8 px-3">
                      Gerer
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
